/**
 * Shared data-logic for the Meta Facebook Page + Pixel pickers (#6163).
 *
 * Two surfaces render these pickers: the campaign wizard's account-setup gate
 * (``routes/_authenticated/new/_layout/campaign/meta.tsx``) and the Settings account-switch modal
 * (``components/glitch-ui/meta-ad-account-selection-modal``). Their *presentation* legitimately
 * differs — an inline wizard gate vs a settings card — so each keeps its own JSX. What was
 * copy-pasted four times, and is easy to get subtly wrong, is the *logic*: the query, the persist
 * mutation, and the auto-select-the-only-option effect with its single-fire ref guard (the bit that
 * had the React-strict-mode double-fire bug). These hooks own that so it lives in one place.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useToast } from '@/hooks/useToast';
import {
  metaPagesQueryOptions,
  metaPixelsQueryOptions,
  useSelectMetaPageMutation,
  useSelectMetaPixelMutation,
} from '@/lib/query-options';
import type { MetaPageInfo, MetaPixelInfo } from '@/lib/types';

interface MetaPagePickerState {
  pages: MetaPageInfo[];
  isLoading: boolean;
  /** A persist (select-page) mutation is in flight — disable the control. */
  isPending: boolean;
  onChange: (pageId: string) => void;
}

/**
 * Facebook Page picker logic. Persists ``page_id``/``page_name`` to the ``MetaAdAccount`` row and
 * invalidates the integration-status query (via ``useSelectMetaPageMutation``).
 */
export function useMetaPagePicker({
  metaAdAccountId,
  currentPageId,
  hasPagesScope,
}: {
  metaAdAccountId: number | null;
  currentPageId: string | null;
  /** ``true`` iff the connected token carries ``pages_show_list`` (chunk 3b). */
  hasPagesScope: boolean;
}): MetaPagePickerState {
  const { toast } = useToast();
  const { data: pages = [], isLoading } = useQuery({
    ...metaPagesQueryOptions(metaAdAccountId),
    // Pre-3b tokens lack pages_show_list, so /me/accounts returns empty regardless — skip the request
    // and let the caller surface the "reconnect" prompt instead of a misleading "no pages" message.
    enabled: !!metaAdAccountId && hasPagesScope,
  });
  const selectPage = useSelectMetaPageMutation();

  const onChange = useCallback(
    (pageId: string) => {
      if (metaAdAccountId == null) return;
      const picked = pages.find((p) => p.id === pageId);
      if (!picked) return;
      selectPage.mutate(
        { meta_ad_account_id: metaAdAccountId, page_id: picked.id, page_name: picked.name },
        {
          // No success toast (house rule): the picker reflecting the chosen Page is the result.
          onError: (error) => {
            toast({
              title: 'Could Not Set Facebook Page',
              description: error instanceof Error ? error.message : 'Please try again.',
              variant: 'destructive',
            });
          },
        },
      );
    },
    [metaAdAccountId, pages, selectPage, toast],
  );

  // Auto-select the only Page (#4653 parity): exactly one Page and none bound yet → pick it without
  // making the user open a one-option dropdown. The ref guard keeps it to a single fire (the select
  // stays in-flight with currentPageId null for a beat, and React strict mode double-invokes effects).
  const autoSelected = useRef(false);
  useEffect(() => {
    if (!autoSelected.current && !currentPageId && pages.length === 1) {
      autoSelected.current = true;
      onChange(pages[0].id);
    }
  }, [pages, currentPageId, onChange]);

  return { pages, isLoading, isPending: selectPage.isPending, onChange };
}

interface MetaPixelPickerState {
  pixels: MetaPixelInfo[];
  isLoading: boolean;
  isPending: boolean;
  onChange: (pixelId: string) => void;
}

/**
 * Meta Pixel picker logic. Persists ``pixel_id``/``pixel_name`` to the ``MetaAdAccount`` row. No
 * scope branch (unlike Pages): Pixels read via the always-granted ``ads_management`` / ``ads_read``.
 */
export function useMetaPixelPicker({
  metaAdAccountId,
  currentPixelId,
}: {
  metaAdAccountId: number | null;
  currentPixelId: string | null;
}): MetaPixelPickerState {
  const { toast } = useToast();
  const { data: pixels = [], isLoading } = useQuery(metaPixelsQueryOptions(metaAdAccountId));
  const selectPixel = useSelectMetaPixelMutation();

  const onChange = useCallback(
    (pixelId: string) => {
      if (metaAdAccountId == null) return;
      const picked = pixels.find((p) => p.id === pixelId);
      selectPixel.mutate(
        { meta_ad_account_id: metaAdAccountId, pixel_id: pixelId, pixel_name: picked?.name ?? null },
        {
          // No success toast (house rule): the picker reflecting the chosen Pixel is the result.
          onError: (error) => {
            toast({
              title: 'Could Not Set Meta Pixel',
              description: error instanceof Error ? error.message : 'Please try again.',
              variant: 'destructive',
            });
          },
        },
      );
    },
    [metaAdAccountId, pixels, selectPixel, toast],
  );

  // Auto-select the only Pixel (#4653): exactly one Pixel and none bound yet → pick it. Same
  // single-fire ref guard as the Page picker above.
  const autoSelected = useRef(false);
  useEffect(() => {
    if (!autoSelected.current && !currentPixelId && pixels.length === 1) {
      autoSelected.current = true;
      onChange(pixels[0].id);
    }
  }, [pixels, currentPixelId, onChange]);

  return { pixels, isLoading, isPending: selectPixel.isPending, onChange };
}
